import { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Download, Loader2, BarChart3 } from "lucide-react";
import { toast } from "sonner";

function fmtMinutes(m) {
  if (!m) return "0m";
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "month", label: "This month" },
];

function rangeFromPreset(preset) {
  const now = new Date();
  if (preset === "today") {
    const d = isoDate(now);
    return { start: d, end: d };
  }
  if (preset === "7d") {
    const start = new Date(now);
    start.setDate(start.getDate() - 6);
    return { start: isoDate(start), end: isoDate(now) };
  }
  if (preset === "30d") {
    const start = new Date(now);
    start.setDate(start.getDate() - 29);
    return { start: isoDate(start), end: isoDate(now) };
  }
  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: isoDate(start), end: isoDate(now) };
  }
  return { start: isoDate(now), end: isoDate(now) };
}

export default function AdminReports() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("all");
  const [range, setRange] = useState(rangeFromPreset("7d"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/admin/employees")
      .then(({ data }) => setEmployees(data))
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { start: range.start, end: range.end };
      if (employeeId && employeeId !== "all") params.employee_id = employeeId;
      const { data } = await api.get("/admin/shifts/report", { params });
      setRows(data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, employeeId]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.total += r.total_minutes || 0;
      acc.brk += r.break_minutes || 0;
      acc.net += r.net_minutes || 0;
      return acc;
    },
    { total: 0, brk: 0, net: 0 }
  );

  // Group by employee for summary
  const byEmployee = {};
  rows.forEach((r) => {
    const key = r.user_id;
    if (!byEmployee[key]) {
      byEmployee[key] = {
        user_id: key,
        name: r.user_name,
        email: r.user_email,
        days: 0,
        total: 0,
        brk: 0,
        net: 0,
      };
    }
    byEmployee[key].days += 1;
    byEmployee[key].total += r.total_minutes || 0;
    byEmployee[key].brk += r.break_minutes || 0;
    byEmployee[key].net += r.net_minutes || 0;
  });
  const summary = Object.values(byEmployee).sort((a, b) => b.net - a.net);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({
        start: range.start,
        end: range.end,
      });
      if (employeeId && employeeId !== "all") params.set("employee_id", employeeId);
      const url = `${API_BASE}/admin/shifts/report/export?${params}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `shifts_${range.start}_to_${range.end}.csv`;
      // Use fetch to honor cookies
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      a.href = URL.createObjectURL(blob);
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("CSV downloaded");
    } catch {
      toast.error("Couldn't export CSV");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-reports">
      {/* Filters */}
      <Card>
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2 space-y-1.5">
            <Label>Quick range</Label>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant="outline"
                  onClick={() => setRange(rangeFromPreset(p.key))}
                  data-testid={`report-preset-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rep-start">Start</Label>
            <Input
              id="rep-start"
              type="date"
              value={range.start}
              onChange={(e) => setRange({ ...range, start: e.target.value })}
              data-testid="report-start-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rep-end">End</Label>
            <Input
              id="rep-end"
              type="date"
              value={range.end}
              onChange={(e) => setRange({ ...range, end: e.target.value })}
              data-testid="report-end-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="report-employee-select">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name || e.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Shifts</div>
            <div className="font-display text-2xl font-bold tabular-nums">
              {rows.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Total clocked</div>
            <div className="font-display text-2xl font-bold tabular-nums">
              {fmtMinutes(totals.total)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Break time</div>
            <div className="font-display text-2xl font-bold tabular-nums">
              {fmtMinutes(totals.brk)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Net hours</div>
            <div className="font-display text-2xl font-bold tabular-nums dp-gradient-text">
              {fmtMinutes(totals.net)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="h-5 w-5" /> Per employee
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={exportCsv}
          data-testid="report-export-csv-btn"
        >
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Per-employee summary */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="hidden sm:table-cell">Email</TableHead>
                <TableHead className="text-right">Days</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Breaks</TableHead>
                <TableHead className="text-right">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin inline-block text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : summary.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-muted-foreground py-10"
                  >
                    No shifts in this range.
                  </TableCell>
                </TableRow>
              ) : (
                summary.map((s) => (
                  <TableRow
                    key={s.user_id}
                    data-testid={`summary-row-${s.user_id}`}
                  >
                    <TableCell className="font-medium">{s.name || "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                      {s.email}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.days}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMinutes(s.total)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMinutes(s.brk)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {fmtMinutes(s.net)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detailed daily rows */}
      <h3 className="font-display text-lg font-semibold pt-2">Daily detail</h3>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>In</TableHead>
                <TableHead>Out</TableHead>
                <TableHead className="text-right">Breaks</TableHead>
                <TableHead className="text-right">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-muted-foreground py-10"
                  >
                    No data.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`detail-row-${r.id}`}>
                    <TableCell className="font-medium">{r.date}</TableCell>
                    <TableCell>{r.user_name || r.user_email}</TableCell>
                    <TableCell className="text-sm">
                      {r.punch_in_at
                        ? new Date(r.punch_in_at).toLocaleTimeString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.punch_out_at
                        ? new Date(r.punch_out_at).toLocaleTimeString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {(r.breaks || []).length} · {fmtMinutes(r.break_minutes)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {fmtMinutes(r.net_minutes)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
